<template>
  <StandardCard :title="t('government.finances.history.title')">
    <div class="chart-container">
      <Bar
        id="credit-history"
        :options="chartOptions"
        :data="chartData"
      />
    </div>
  </StandardCard>
</template>

<script setup lang="ts">
import { Bar } from 'vue-chartjs'
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale
} from 'chart.js'
import { onMounted, ref, watch } from 'vue'
import Backend from '@/models/backend'
import { useGameStore } from '@/stores/gameStore'
import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import { useI18n } from 'vue-i18n'

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)

interface ChartDataResponse {
  labels: string[]
  data: number[]
}

const { t } = useI18n()
const gameStore = useGameStore()

const chartData = ref({
  labels: [] as string[],
  datasets: [] as { data: number[]; backgroundColor: string[]; categoryPercentage: number; barPercentage: number }[]
})

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: 'x' as const,
  scales: {
    x: {
      grid: {
        display: false
      },
      ticks: {
        color: '#475569',
        align: 'start' as const
      }
    },
    y: {
      beginAtZero: true,
      grid: {
        display: true,
        color: '#475569'
      },
      ticks: {
        color: '#475569'
      }
    }
  },
  layout: {
    padding: {
      left: 0
    }
  },
  plugins: {
    legend: {
      display: false,
      labels: {
        color: '#475569',
        font: {
          size: 14
        }
      }
    }
  },
  animation: false as const
}

const loadChartData = async () => {
  const backend = new Backend()
  const response = (await backend.getFinancialIncomeChartData()) as unknown as ChartDataResponse
  if (response) {
    chartData.value = {
      labels: response.labels,
      datasets: [
        {
          data: response.data,
          backgroundColor: response.data.map((value: number) => (value >= 0 ? '#22c55e' : '#ef4444')),
          categoryPercentage: 1,
          barPercentage: 0.8
        }
      ]
    }
  }
}

onMounted(() => {
  loadChartData()
})

watch(
  () => gameStore.turn,
  () => {
    loadChartData()
  }
)
</script>

<style>
.chart-container {
  width: 100%;
  height: 20rem; /* Fixed height to prevent resizing issues */
}
</style>
